import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import { HHDSImageTagName } from '../Image/Image';

const DEBUG_VERBOSE: boolean = false;
export const HHDSDominoCardTagName: string = 'hhds-domino-card';
const TAG_NAME: string = HHDSDominoCardTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSDominoCardAttrNames = {
	image: 'image',
	srcSet: 'srcset',
	sizes: 'sizes',
	indexNum: 'index-num',
	imageAspect: 'image-aspect',
	imageOnTop: 'image-on-top',
	flipHorizontal: 'flip-horizontal',
	topDivider: 'top-divider',
	bottomDivider: 'bottom-divider',
	dominoStyle: 'domino-style',
	alt: 'alt',
	parallax: 'parallax',
	textLayout: 'text-layout',
};

export enum HHDSDominoCardTextLayoutType {
	a = 'a',
	b = 'b',
}

const Attrs = HHDSDominoCardAttrNames;

export class HHDSDominoCard extends Component {
	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const indexNum: string = this.vars.get<string>(Attrs.indexNum);

		const flipHorizontal: boolean = this.vars.get<boolean>(Attrs.flipHorizontal);
		const imageOnTop: boolean = this.vars.get<boolean>(Attrs.imageOnTop);

		let imageryClasses: string[] = [];
		let detailClasses: string[] = [];
		let leftClasses: string[] = [];
		let componentClasses: string[] = [];

		let indexNumberHtml: string = '';
		let spacerTopHtml: string = '';
		let spacerBottomHtml: string = '';
		if (indexNum?.length) {
			let flipClass: string = flipHorizontal ? `${TAG_NAME}__index-num--flip-horizontal` : '';
			let imageOnTopClass: string = imageOnTop ? `${TAG_NAME}__index-num--image-on-top` : '';
			indexNumberHtml = `<h3 class="${TAG_NAME}__index-num ${flipClass} ${imageOnTopClass} heading heading--05 animate">${indexNum}</h3>`;
			spacerTopHtml = `<div class="${TAG_NAME}__left__spacer-top"></div>`;
			spacerBottomHtml = `<div class="${TAG_NAME}__left__spacer-bottom"></div>`;
			detailClasses.push(`${TAG_NAME}__left--space-between`);
		}

		if (this.vars.get<boolean>(Attrs.imageOnTop)) {
			imageryClasses.push(`${TAG_NAME}__imagery--image-on-top`);
			leftClasses.push(`${TAG_NAME}__left--image-on-top`);
			detailClasses.push(`${TAG_NAME}__detail--image-on-top`);
			componentClasses.push(`${TAG_NAME}--image-on-top`);
		}

		if (!indexNum?.length) {
			componentClasses.push(`${TAG_NAME}--no-index`);
		}

		const textLayout: HHDSDominoCardTextLayoutType = this.vars.get<HHDSDominoCardTextLayoutType>(Attrs.textLayout);
		const isLayoutB: boolean = textLayout === HHDSDominoCardTextLayoutType.b;

		let dividerHtml: string = '';
		if (isLayoutB) {
			detailClasses.push(`${TAG_NAME}__detail--layout-b`);
			dividerHtml = `<div class="${TAG_NAME}__divider animate"></div>`;
		}

		if (flipHorizontal) {
			leftClasses.push(`${TAG_NAME}__left--flip-horizontal`);
			imageryClasses.push(`${TAG_NAME}__imagery--flip-horizontal`);
			detailClasses.push(`${TAG_NAME}__detail--flip-horizontal`);
			componentClasses.push(`${TAG_NAME}--flip-horizontal`);
		}

		let borderStyles: string[] = [];
		if (this.vars.get<boolean>(Attrs.topDivider)) {
			borderStyles.push('border-top: 1px solid var(--color-tint-coconut-10) !important;');
		}
		if (this.vars.get<boolean>(Attrs.bottomDivider)) {
			borderStyles.push('border-bottom: 1px solid var(--color-tint-coconut-10) !important;');
		}

		const dominoStyle: boolean = this.vars.get<boolean>(Attrs.dominoStyle);

		let containerClass: string = '';
		let componentStyles: string[] = [];
		if (dominoStyle) {
			containerClass = 'container';
		} else {
			detailClasses.push(`${TAG_NAME}__detail--non-domino`);
			componentClasses.push(`${TAG_NAME}--non-domino`);
			if (flipHorizontal) detailClasses.push(`${TAG_NAME}__detail--non-domino-flip-horizontal`);
			componentStyles.push('--domino-padding-top: 0px');
			componentStyles.push('--domino-padding-bottom: 0px');
		}

		const html: string = `
			<div class="${containerClass}" style="${borderStyles.join(' ')}">
				<div class="${TAG_NAME} ${componentClasses.join(' ')}" style="${componentStyles.join('; ')}">
					${indexNumberHtml}
					<div class="${TAG_NAME}__left ${leftClasses.join(' ')}">
						${spacerTopHtml}
						<div class="${TAG_NAME}__left__mid ${TAG_NAME}__detail ${detailClasses.join(' ')}">
							<h3 class="${TAG_NAME}__header heading heading--03 animate"><slot name="header"></h3>
							<h6 class="${TAG_NAME}__subtitle heading heading--06 animate"><slot name="subtitle"></slot></h6>
							${dividerHtml}
							<p class="${TAG_NAME}__body paragraph animate"><slot name="body"></slot></p>
							<slot name="cta"></slot>
						</div>
						${spacerBottomHtml}
					</div>
					<div class="${TAG_NAME}__imagery ${imageryClasses.join(' ')}">
						${this.imageHtml}
					</div>
				</div>
			</div>
    `;

		this.setInnerHtml('', '', html);

		const subtitle = this.querySelector<HTMLElement>(`.${TAG_NAME}__subtitle`);
		if (subtitle?.innerText?.length === 0) subtitle.remove();

		this.observeSlotChanges(true);
	}

	private get imageHtml(): string {
		const src = this.vars.get<string>(Attrs.image);
		const srcset = this.vars.get<string>(Attrs.srcSet);
		const sizes = this.vars.get<string>(Attrs.sizes);
		const alt = this.vars.get<string>(Attrs.alt);
		const aspect: string = this.vars.get<string>(Attrs.imageAspect);
		let styleAttr: string = '';
		if (aspect != 'intrinsic') styleAttr = `style="--image-aspect-ratio: ${aspect};"`;

		const useParallax: boolean = this.vars.get<boolean>(Attrs.parallax);
		const parallaxAttrs: string = useParallax ? `parallax-reverse="false" parallax-strength="1.2"` : '';
		if (srcset?.length && sizes?.length) {
			return `<${HHDSImageTagName} ${parallaxAttrs} srcset="${srcset}" sizes="${sizes}" alt="${alt}" ${styleAttr}></${HHDSImageTagName}>`;
		} else if (src?.length) {
			return `<${HHDSImageTagName} ${parallaxAttrs} src="${src}" alt="${alt}" ${styleAttr}></${HHDSImageTagName}>`;
		}
		return '';
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange:`, _elements);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.imageAspect]: {
		description: 'Aspect ratio of the image.',
		defaultValue: '16/9',
		type: String,
	},
	[Attrs.image]: {
		description: 'Image source.',
		defaultValue: '',
		type: String,
	},
	[Attrs.imageOnTop]: {
		description: 'Whether the image should be displayed at the top on mobile layout.',
		defaultValue: false,
		type: Boolean,
	},
	[Attrs.flipHorizontal]: {
		description: 'Whether the layout should be flipped horizontally.',
		defaultValue: false,
		type: Boolean,
	},
	[Attrs.topDivider]: {
		description: 'Whether the card should have a top divider.',
		type: Boolean,
		defaultValue: false,
	},
	[Attrs.bottomDivider]: {
		description: 'Whether the card should have a bottom divider.',
		type: Boolean,
		defaultValue: false,
	},
	[Attrs.indexNum]: {
		description: 'Index number.',
		type: String,
		defaultValue: '',
	},
	[Attrs.srcSet]: {
		description: 'Image srcset.',
		type: String,
		defaultValue: '',
	},
	[Attrs.sizes]: {
		description: 'Image sizes.',
		type: String,
		defaultValue: '',
	},
	[Attrs.dominoStyle]: {
		description: 'Whether to use domino padding & container styles',
		type: Boolean,
		defaultValue: true,
	},
	[Attrs.alt]: {
		description: 'Image alt text.',
		type: String,
		defaultValue: '',
	},
	[Attrs.parallax]: {
		description: 'Whether to enable parallax scrolling.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.textLayout]: {
		description: 'Text layout type for the card.',
		defaultValue: HHDSDominoCardTextLayoutType.a,
		type: HHDSDominoCardTextLayoutType,
		typeString: 'HHDSDominoCardTextLayoutType',
	},
};
